// src/features/fx-ranking/FXRankingPage.tsx
//
// FX Ranking feature page. Sibling of
// features/commodities-ranking/CommoditiesRankingPage. Loads the per-tier
// factor aggregates from GET /api/v1/fx-ranking/tier-summary once (drives
// the radar + the tier filter pill list), and the ranked cross-section from
// GET /api/v1/fx-ranking/universe (optionally narrowed by tier server-side).
// Sorting is client-side. A single batched call to
// GET /api/v1/fx-ranking/historical-batch powers the table sparklines.
// Clicking a ticker opens the COT positioning pop-over (currencies without
// CFTC coverage show an informative empty state).

import { useEffect, useMemo, useState } from 'react';

import { Layout, Sidebar } from '../../shared/ui';
import {
    fetchHistoricalBatch,
    fetchTierSummary,
    fetchUniverse,
    type HistoricalBatchResponse,
    type TierSummaryRow,
    type UniverseRow,
} from './api';
import { FXDetailPopup } from './components/FXDetailPopup';
import { RiskReturnScatter } from './components/RiskReturnScatter';
import { TierRadar } from './components/TierRadar';
import { UniverseTable, type SortKey } from './components/UniverseTable';

const SPARKLINE_LIMIT = 60;

export const FXRankingPage = () => {
    const [universe, setUniverse] = useState<UniverseRow[]>([]);
    const [tierSummary, setTierSummary] = useState<TierSummaryRow[]>([]);
    const [asOfDate, setAsOfDate] = useState<string>('');
    const [isLoading, setIsLoading] = useState(true);
    const [isUniverseLoading, setIsUniverseLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [activeTier, setActiveTier] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('universe_rank');
    const [sortDesc, setSortDesc] = useState<boolean>(false);
    const [hoverSymbol, setHoverSymbol] = useState<string | null>(null);
    const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);

    const [sparklines, setSparklines] = useState<HistoricalBatchResponse>({});

    // --- Tier aggregates: loaded ONCE on mount. -------------------------
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchTierSummary();
                if (!cancelled) setTierSummary(res.tiers);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Failed to load tier summary', err);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // --- Universe: SERVER-SIDE refetch whenever the tier filter changes. --
    useEffect(() => {
        let cancelled = false;
        setIsUniverseLoading(true);
        (async () => {
            try {
                const res = await fetchUniverse(activeTier ?? undefined);
                if (cancelled) return;
                setUniverse(res.items);
                setAsOfDate(res.as_of_date);
                setError(null);
            } catch (err) {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error('Failed to load FX ranking universe', err);
                setError(
                    'Unable to load FX rankings right now. Please try again shortly.',
                );
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                    setIsUniverseLoading(false);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeTier]);

    // --- Distinct tiers for the filter pills (stable canonical order). ---
    const tiers = useMemo(
        () => tierSummary.map((t) => t.tier),
        [tierSummary],
    );

    const totalUniverseSize = useMemo(
        () => tierSummary.reduce((acc, t) => acc + (t.count ?? 0), 0),
        [tierSummary],
    );

    // --- Client-side SORT (filtering is server-side). -------------------
    const processed = useMemo(() => {
        const sorted = [...universe].sort((a, b) => {
            const va = a[sortKey];
            const vb = b[sortKey];
            let cmp: number;
            if (typeof va === 'string' && typeof vb === 'string') {
                cmp = va.localeCompare(vb);
            } else {
                cmp = (va as number) - (vb as number);
            }
            return sortDesc ? -cmp : cmp;
        });
        return sorted;
    }, [universe, sortKey, sortDesc]);

    // --- Symbols whose sparklines we batch-fetch (top of current view). --
    const sparklineSymbols = useMemo(
        () => processed.slice(0, SPARKLINE_LIMIT).map((r) => r.symbol),
        [processed],
    );
    const sparklineSymbolSet = useMemo(
        () => new Set(sparklineSymbols),
        [sparklineSymbols],
    );

    // --- Batched sparkline fetch. ----------------------------------------
    useEffect(() => {
        if (sparklineSymbols.length === 0) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetchHistoricalBatch(sparklineSymbols, 90);
                if (cancelled) return;
                setSparklines((prev) => ({ ...prev, ...res }));
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('Failed to load sparkline history', err);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sparklineSymbols.join(',')]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDesc((d) => !d);
        } else {
            setSortKey(key);
            setSortDesc(
                key !== 'universe_rank' &&
                    key !== 'symbol' &&
                    key !== 'name' &&
                    key !== 'tier',
            );
        }
    };

    const pillBase =
        'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer';
    const pillActive =
        'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900';
    const pillInactive =
        'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700';

    return (
        <Layout
            sidebar={<Sidebar />}
            title="FX Rankings"
            subtitle="Cross-sectional momentum + COT-positioning scores for the 30-currency institutional FX universe."
        >
            {/* Header row: tier filter pills + freshness badge. */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        className={`${pillBase} ${!activeTier ? pillActive : pillInactive}`}
                        onClick={() => setActiveTier(null)}
                    >
                        All Tiers
                        {totalUniverseSize > 0 && (
                            <span className="ml-1.5 opacity-70">
                                {totalUniverseSize}
                            </span>
                        )}
                    </button>
                    {tiers.map((t) => (
                        <button
                            key={t}
                            type="button"
                            className={`${pillBase} ${activeTier === t ? pillActive : pillInactive}`}
                            onClick={() => setActiveTier(t)}
                        >
                            {t}
                        </button>
                    ))}
                </div>
                {asOfDate && (
                    <span className="text-xs font-mono text-slate-500 bg-slate-100 dark:bg-slate-800/50 px-2 py-1 rounded">
                        Updated: {asOfDate}
                    </span>
                )}
            </div>

            {error && (
                <div className="mb-6 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                    {error}
                </div>
            )}

            {isLoading ? (
                <div className="h-64 flex items-center justify-center text-slate-500 dark:text-slate-400">
                    Loading quantitative data…
                </div>
            ) : (
                <>
                    {/* Charts: cross-sectional Risk/Return + tier radar. */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Risk vs Return
                            </h2>
                            <p className="muted text-xs mb-4">
                                {activeTier ?? 'All tiers'} ·{' '}
                                {processed.length} currencies
                            </p>
                            <RiskReturnScatter
                                data={processed}
                                highlightSymbol={hoverSymbol}
                            />
                        </div>
                        <div className="surface-card">
                            <h2 className="section-title mb-1">
                                Tier Factor Radar
                            </h2>
                            <p className="muted text-xs mb-4">
                                Average z-scores by liquidity tier
                            </p>
                            <TierRadar tiers={tierSummary} />
                        </div>
                    </div>

                    {/* Full sortable universe table. Dimmed while a
                        server-side tier refetch is in flight. */}
                    <div
                        className={`transition-opacity ${isUniverseLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
                        aria-busy={isUniverseLoading}
                    >
                        <UniverseTable
                            data={processed}
                            sparklines={sparklines}
                            sparklineSymbols={sparklineSymbolSet}
                            sortKey={sortKey}
                            sortDesc={sortDesc}
                            onSort={handleSort}
                            onRowHover={setHoverSymbol}
                            onSymbolClick={setSelectedSymbol}
                        />
                    </div>
                </>
            )}

            {/* Tabbed detail pop-over (Price / Signal / Factors / COT) for
                the clicked ticker. */}
            {selectedSymbol && (
                <FXDetailPopup
                    symbol={selectedSymbol}
                    onClose={() => setSelectedSymbol(null)}
                />
            )}
        </Layout>
    );
};

export default FXRankingPage;
